import * as React from "react";
import {useEffect} from "react";
import {workingConfig} from "../../ConfigSignal";
import {Button, Dropdown, Label, Tab} from "semantic-ui-react";
import {ApiGatewayConfig, CompanyWithConfig} from "../../../../../../generated/api";
import {ConfigContext} from "../../../../context/ConfigContext";
import {clampTabIndex} from "../../../../context/TabSession";
import AceEditor from "react-ace";
import {LogView} from "../../../logView/LogView";
import {v4 as uuid} from "uuid";
import {findDisplayName} from "../newNavTree/NavTree";
import {objectToDropDownOptionsWithDisplayNames} from "../../ScriptConverterPlayground/ScriptDropdown";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";

export function ApiGatewayTester() {

    const context = React.useContext(ConfigContext);

    const [availableApiGateways, setAvailableApiGateways] = React.useState<string[]>([])
    const [index, setIndex] = React.useState(0)
    const [selectedApiGateway, setSelectedApiGateway] = React.useState<string>(null)
    const [testResult, setTestResult] = React.useState<any>(null)
    const [loading, setLoading] = React.useState(false)
    const [xReqId, setXReqId] = React.useState<string>(null)

    useEffect(() => {
        try {
            let gateways = []
            JSON.parse(workingConfig.value)
                .apiGatewayConfigs
                .map((apiGatewayConfig: ApiGatewayConfig) => {
                    gateways.push(objectToDropDownOptionsWithDisplayNames(apiGatewayConfig))
                })
            setAvailableApiGateways(gateways)
            setSelectedApiGateway(gateways[0])
        } catch (ex) {
            console.log("unable to load api gateways from config")
        }
    }, [])


    async function testApiGateway() {
        setLoading(true)
        const requestId = uuid();
        setXReqId(requestId);
        setTestResult({loading: true}); // Zeige Logs sofort an

        try {
            let companyWithConfig = {} as CompanyWithConfig
            companyWithConfig.companyConfig = JSON.parse(workingConfig.value)
            companyWithConfig.companyId = context.companyId
            companyWithConfig.name = context.companyName
            companyWithConfig.tag = context.companyTag

            let auth = backend.withTokenAuthHeader(authentication.token)
           
            let response = await fetch("/api/internal/testApiGatewayConfig/" + index.toString(), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-request-id": requestId,
                    "authorization": auth.headers.Authorization.toString()
                },
                body: JSON.stringify(companyWithConfig)
            })

            if (!response.ok) {
                let errorText = "";
                try {
                    errorText = await response.text();
                } catch (e) {
                    errorText = response.statusText;
                }
                const errorMessage = `HTTP ${response.status}: ${errorText || response.statusText}`;
                console.error("API Gateway Test failed:", errorMessage);
                setTestResult({
                    importError: errorMessage,
                    exportError: null,
                    importResults: null,
                    exportResults: null,
                    logs: null
                });
                setLoading(false);
                return;
            }

            let responseJson = await response.json()
            setTestResult(responseJson)
        } catch (ex) {
            console.log("error testing api gateway", ex)
            const errorMessage = ex instanceof Error ? ex.message : "Failed to test API Gateway";
            setTestResult({
                importError: errorMessage,
                exportError: null,
                importResults: null,
                exportResults: null,
                logs: null
            })
        } finally {
            setLoading(false)
        }
    }

    function hasExportErrors(exportResults: any): boolean {
        if (!exportResults || !Array.isArray(exportResults)) {
            return false;
        }
        return exportResults.some((result: any) => result.status !== "OK");
    }


    function drawTestResult() {
        if (testResult == null) {
            return null
        }

        const isLoading = testResult.loading === true;
        const importHasError = testResult.importError != null;
        const exportHasError = testResult.exportError != null || hasExportErrors(testResult.exportResults);
        const exportIsEmpty = Array.isArray(testResult.exportResults) && testResult.exportResults.length === 0;

        const importColor = importHasError ? '#db2828' : (isLoading ? '#767676' : '#21ba45');
        const exportColor = exportHasError ? '#db2828' : (exportIsEmpty ? '#f2711c' : (testResult.exportResults != null ? '#21ba45' : '#767676'));

        const panes = [
            {
                menuItem: {
                    key: 'import',
                    icon: importHasError ? 'times circle' : (isLoading ? 'circle notched' : 'check circle'),
                    content: 'Import Results',
                    style: {color: importColor}
                },
                render: () => <Tab.Pane style={{height: 'calc(100vh - 300px)', overflowY: 'auto'}}>
                    {isLoading ? <p>Loading...</p> : drawImportResult(testResult.importResults, testResult.importError)}
                </Tab.Pane>
            },
            {
                menuItem: {
                    key: 'export',
                    icon: exportHasError ? 'times circle' : (isLoading ? 'circle notched' : (exportIsEmpty ? 'exclamation triangle' : (testResult.exportResults != null ? 'check circle' : 'minus circle'))),
                    content: 'Export Results',
                    style: {color: exportColor}
                },
                render: () => <Tab.Pane style={{height: 'calc(100vh - 300px)', overflowY: 'auto'}}>
                    {isLoading ? <p>Loading...</p> : drawExportResult(testResult.exportResults, testResult.exportError)}
                </Tab.Pane>
            },
            {
                menuItem: {
                    key: 'logs',
                    icon: 'file alternate outline',
                    content: 'Logs'
                },
                render: () => <Tab.Pane
                    style={{
                        height: 'calc(100vh - 300px)',
                        overflowY: 'auto'
                    }}>
                    {logView()}
                </Tab.Pane>
            }
        ]

        return <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 250px)'
        }}>
            <style>{`
                .ui.tab .menu .item[data-tab="0"] { color: ${importColor} !important; }
                .ui.tab .menu .item[data-tab="0"] i { color: ${importColor} !important; }
                .ui.tab .menu .item[data-tab="1"] { color: ${exportColor} !important; }
                .ui.tab .menu .item[data-tab="1"] i { color: ${exportColor} !important; }
            `}</style>
            <Tab
                panes={panes}
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }}
                defaultActiveIndex={clampTabIndex(context.tabSession.apiGatewayTesterTab, panes)}
                onTabChange={(_, data) => {
                    context.setSingleTabSession("apiGatewayTesterTab", parseInt(data.activeIndex.toString()))
                }}/>
        </div>
    }

    function drawImportResult(importResults: any, importError: string) {

        if (importError != null) {
            return <>
                <Label color="red" size="large">Import Error:</Label>
                {ace(importError)}
            </>
        }

        return <>
            <Label color="green" size="large">Import Results:</Label>
            {ace(JSON.stringify(importResults, null, 2))}
        </>

    }

    function drawExportResult(exportResults: any, exportError: string) {
        if (exportError != null) {
            return <>
                <Label color="red" size="large">Export Error:</Label>
                {ace(exportError)}
            </>
        }
        if (exportResults != null) {
            const hasErrors = hasExportErrors(exportResults);
            const isEmpty = Array.isArray(exportResults) && exportResults.length === 0;

            let labelColor: "red" | "orange" | "green" = "green";
            let labelText = "Export Results:";

            if (hasErrors) {
                labelColor = "red";
                labelText = "Export Results (with errors):";
            } else if (isEmpty) {
                labelColor = "orange";
                labelText = "Export Results (empty):";
            }

            return <>
                <Label color={labelColor} size="large">
                    {labelText}
                </Label>
                {ace(JSON.stringify(exportResults, null, 2))}
            </>
        }
        return <p>No export results available</p>
    }

    function logView() {
        if(xReqId != null && xReqId !== "") {
            return <LogView
                query={xReqId}
                initialTimespan={null}
                origin={"APIGatewayTester"}
                responseLogs={testResult?.logs}
                autoUpdate={loading}
                autoUpdateInterval={1000}
            />
        }
        return <p>No xRequestId for logs available, yet</p>
    }

    function ace(value: string) {
        return <AceEditor
            mode="json"
            theme="monokai"
            name="importResultEditor"
            width={"100%"}
            height="calc(100vh - 400px)"
            value={value}
            editorProps={{$blockScrolling: true}}
        />
    }

    return <>
        <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px'}}>
            <Dropdown
                placeholder='Select API Gateway'
                selection
                style={{flex: 1}}
                disabled={loading}
                options={availableApiGateways.map(apiGateway => ({
                    key: apiGateway,
                    text: apiGateway,
                    value: apiGateway
                }))}
                value={selectedApiGateway ?? availableApiGateways[0] ?? null}
                onChange={(e, data) => {
                    setSelectedApiGateway(data.value as string)
                    setIndex(availableApiGateways.findIndex(apiGateway => apiGateway === data.value))
                    setTestResult(null)
                }}
            />

            <Button
                disabled={selectedApiGateway == null}
                primary
                loading={loading}
                onClick={() => testApiGateway().then()}
            >
                Test
            </Button>
        </div>

        {drawTestResult()}
    </>
}